export interface MinimapTranslation {
  x: number;
  y: number;
}

export interface MinimapTranslationBounds {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

export interface MinimapDimensions {
  scale?: number;
  translation?: Partial<MinimapTranslation>;
  minScale?: number;
  translationBounds?: Partial<MinimapTranslationBounds>;
}

export interface MinimapViewportMetrics {
  viewportWidth: number;
  viewportHeight: number;
  contentWidth: number;
  contentHeight: number;
}

export interface NormalizedMinimapDimensions {
  scale: number;
  translation: MinimapTranslation;
  minScale: number;
  translationBounds: MinimapTranslationBounds;
}

export interface PlayerInspectionState {
  stepId: number | null;
}

export type PlayerInspectionEvent =
  | { type: 'player-action'; action: unknown; enabled: boolean }
  | { type: 'return' }
  | { type: 'reset' };

export const initialPlayerInspectionState: PlayerInspectionState = { stepId: null };

const EPSILON = 0.001;

/** Fixed top padding (px) applied to the minimap so content always starts near the top edge. */
export const MINIMAP_TOP_PADDING = 12;

export function normalizeMinimapDimensions(
  current: MinimapDimensions | undefined,
  metrics: MinimapViewportMetrics,
): NormalizedMinimapDimensions {
  const fitScale = computeFitScale(metrics);
  const scale = Math.max(current?.scale ?? 1, fitScale);
  const translationBounds = computeTranslationBounds(metrics, scale);
  const translation = clampTranslation(
    {
      x: current?.translation?.x ?? translationBounds.xMax,
      y: current?.translation?.y ?? translationBounds.yMax,
    },
    translationBounds,
  );

  return {
    scale,
    minScale: fitScale,
    translation,
    translationBounds,
  };
}

export function computeFitScale(metrics: MinimapViewportMetrics) {
  if (
    metrics.viewportWidth <= 0 ||
    metrics.viewportHeight <= 0 ||
    metrics.contentWidth <= 0 ||
    metrics.contentHeight <= 0
  ) {
    return 1;
  }

  return Math.min(
    1,
    metrics.viewportWidth / metrics.contentWidth,
    metrics.viewportHeight / metrics.contentHeight,
  );
}

export function computeTranslationBounds(
  metrics: MinimapViewportMetrics,
  scale: number,
): MinimapTranslationBounds {
  const scaledWidth = metrics.contentWidth * scale;
  const scaledHeight = metrics.contentHeight * scale;

  // Horizontal: center when content fits, allow scrolling when it overflows.
  const xMin = computeAxisMin(metrics.viewportWidth, scaledWidth);
  const xMax = computeAxisMax(metrics.viewportWidth, scaledWidth);

  // Vertical: always pin the top of the content to MINIMAP_TOP_PADDING.
  // Only allow vertical dragging when the content actually overflows below the fold.
  const yMax = MINIMAP_TOP_PADDING;
  const yMin =
    scaledHeight + MINIMAP_TOP_PADDING <= metrics.viewportHeight
      ? MINIMAP_TOP_PADDING // fits — lock in place
      : metrics.viewportHeight - scaledHeight; // overflows — allow scrolling to the bottom

  return { xMin, xMax, yMin, yMax };
}

export function clampTranslation(
  translation: MinimapTranslation,
  bounds: MinimapTranslationBounds,
): MinimapTranslation {
  return {
    x: clamp(translation.x, bounds.xMin, bounds.xMax),
    y: clamp(translation.y, bounds.yMin, bounds.yMax),
  };
}

export function areMinimapDimensionsEqual(
  left: MinimapDimensions | undefined,
  right: MinimapDimensions | undefined,
) {
  if (!left || !right) {
    return false;
  }

  return (
    nearlyEqual(left.scale ?? 0, right.scale ?? 0) &&
    nearlyEqual(left.minScale ?? 0, right.minScale ?? 0) &&
    nearlyEqual(left.translation?.x ?? 0, right.translation?.x ?? 0) &&
    nearlyEqual(left.translation?.y ?? 0, right.translation?.y ?? 0) &&
    nearlyEqual(left.translationBounds?.xMin ?? 0, right.translationBounds?.xMin ?? 0) &&
    nearlyEqual(left.translationBounds?.xMax ?? 0, right.translationBounds?.xMax ?? 0) &&
    nearlyEqual(left.translationBounds?.yMin ?? 0, right.translationBounds?.yMin ?? 0) &&
    nearlyEqual(left.translationBounds?.yMax ?? 0, right.translationBounds?.yMax ?? 0)
  );
}

/**
 * Keeps author-side graph inspection independent from the player's learner
 * queue. A minimap jump enters inspection; every learner navigation action is
 * intentionally ignored by this state machine and continues through Redux.
 *
 * **At most one step is ever expanded, and clicking the open one closes it** (owner decision
 * 2026-08-06). The single-slot state is what enforces the first half: expanding a second card
 * cannot leave the first open, because there is nowhere to record two. The second half is the
 * `stepId === state.stepId` branch below — without it a reviewer who clicked the wrong card had no
 * way back except the Return button, and re-clicking the card they were already reading did
 * nothing at all, which reads as a broken control.
 *
 * One expanded card is a product constraint, not a technical one: the graph lives in a strip a few
 * hundred pixels tall, and two expanded cards in it are unreadable.
 */
export function reducePlayerInspectionState(
  state: PlayerInspectionState,
  event: PlayerInspectionEvent,
): PlayerInspectionState {
  if (event.type === 'return' || event.type === 'reset') {
    return initialPlayerInspectionState;
  }

  const stepId = getInspectionStepIdFromPlayerAction(event.action, event.enabled);
  if (stepId === null) {
    return state;
  }

  return stepId === state.stepId ? initialPlayerInspectionState : { stepId };
}

/**
 * Author-side read-only inspection is only ever triggered by a MINIMAP node click. The player
 * reuses one `/JUMP_TO` action for both that and its own learner navigation ("Go back to step" in
 * the editable step actions, "Go to step N" in the queue), so the action type alone cannot tell
 * them apart — inferring from the type swallowed the reviewer's back action and left them in the
 * inspection dialog (session 05, S5-T1).
 *
 * The payloads do differ, and that is the origin signal. A cross-model enumeration of every
 * `jumpTo` dispatch site in the pinned bundle (2026-07-29) found six origins:
 *
 *   1. editable step action "Go back to step"  `{ stepId, stepInstanceId }`
 *   2. step-header parent/child relation link  `{ stepId, stepInstanceId }`
 *   3. response-link "Go to step N"            `{ stepId }`
 *   4. minimap node click                      `{ stepId, shouldStartEditing: false }`
 *   5. "Step N was created" notification link  `{ stepId }`
 *   6. steps-manager "Go to"                   `{ stepId, shouldStartEditing: true }`
 *
 * Only 4 is ours to intercept, and only 4 carries `shouldStartEditing: false` — the author embed
 * hard-configures `minimap.is_click_to_edit_enabled: false` (see `PlayerPreview`), which the player
 * forwards verbatim as that flag, while the one other `shouldStartEditing` caller sends literal
 * `true`. Requiring the exact `false` plus an absent `stepInstanceId` therefore matches the minimap
 * and nothing else. Merely testing for the property's presence would swallow origin 6.
 *
 * The strictness is deliberate: wrongly swallowing a navigation breaks the item under review, while
 * wrongly forwarding a minimap click only skips the read-only overlay. If click-to-edit is ever
 * enabled for the author embed, this predicate must be revisited — the minimap would then emit
 * `true` and inspection would silently stop engaging.
 */
export function getInspectionStepIdFromPlayerAction(action: unknown, enabled: boolean): number | null {
  if (!enabled || !isRecord(action) || typeof action.type !== 'string' || !action.type.endsWith('/JUMP_TO')) {
    return null;
  }

  const payload = action.payload;
  if (!isRecord(payload) || typeof payload.stepId !== 'number' || !Number.isFinite(payload.stepId)) {
    return null;
  }

  if (!isMinimapOriginPayload(payload)) {
    return null;
  }

  return payload.stepId;
}

/**
 * True only for the minimap's own jump payload shape — see `getInspectionStepIdFromPlayerAction`.
 *
 * `stepInstanceId` is tested for literal key absence, not for a nullish value. Anything that names
 * a step instance at all — even with an undefined value — is player-originated navigation, and the
 * safe direction here is to forward rather than swallow. No current bundle origin emits both
 * markers, so this only decides hypothetical payloads; it decides them conservatively.
 */
function isMinimapOriginPayload(payload: Record<string, unknown>): boolean {
  return payload.shouldStartEditing === false && !('stepInstanceId' in payload);
}

function computeAxisMin(viewportSize: number, scaledContentSize: number) {
  if (scaledContentSize <= viewportSize) {
    return (viewportSize - scaledContentSize) / 2;
  }

  return viewportSize - scaledContentSize;
}

function computeAxisMax(viewportSize: number, scaledContentSize: number) {
  if (scaledContentSize <= viewportSize) {
    return (viewportSize - scaledContentSize) / 2;
  }

  return 0;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function nearlyEqual(left: number, right: number) {
  return Math.abs(left - right) <= EPSILON;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
